import {
  CnpjOrCpfInput,
  convertEventSelectToTarget,
  formatDataToLabelValue,
  FormFeedBack,
  getZipCodeNumOfDigits,
  OverlineTitle,
  removeCnpjMask,
  removeZipCodeMask,
  Select,
  SelectRemote,
  STATUSPAGE,
  ZipCodeInput,
} from '@repo/ui';
import { TelPhone } from 'adasi_components';
import { SearchByCnpjResponse } from 'domain/models';
import {
  DeleteEntitiesUseCaseInterface,
  GetAddressViaZipCodeUseCaseInterface,
  GetSearchByCnpjUseCaseInterface,
} from 'domain/usecases';
import { EntityType } from 'domain/value-objects/entities-type';
import { FormikValues } from 'formik';
import { ModalArchive } from 'presentation/app/components/modal-archive';
import React, { SetStateAction, Dispatch } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Col, Input, Label, Row } from 'reactstrap';

interface IProps {
  validation: FormikValues;
  requestSearchByCnpj: (params: GetSearchByCnpjUseCaseInterface.Params) => void;
  requestGetAddressViaZipCode: (
    params: GetAddressViaZipCodeUseCaseInterface.Params,
  ) => void;
  requestEntitiesDelete: (
    params: DeleteEntitiesUseCaseInterface.Params,
  ) => Promise<void>;
  loadingSearchByCnpj: STATUSPAGE;
  searchByCnpjData: SearchByCnpjResponse;
  citiesForSelectOptionsData: any[];
  setCitiesForSelectOptionsData: Dispatch<SetStateAction<any[]>>;
  modalDeleteState: boolean;
  entityId: string;
  zipCodeSchema: any;
  marginYButton: string;
  requestGetAllCitiesForSelectOptions: any;
  setModalDeleteState: Dispatch<SetStateAction<boolean>>;
  options: {
    label: string;
    value: EntityType;
  }[];
  hasChangedAddressFields: any;
  loadingArchive: boolean;
  loading?: boolean;
}

const applyDayMonthMask = (value: string) => {
  const onlyDigits = value.replace(/\D/g, '').slice(0, 4);
  if (onlyDigits.length <= 2) return onlyDigits;
  return `${onlyDigits.slice(0, 2)}/${onlyDigits.slice(2)}`;
};

const skeletonBaseColor = '#e8e8e8c5';
const skeletonHighlightColor = '#bababa96';

const FieldSkeleton = () => (
  <Skeleton
    height={38}
    baseColor={skeletonBaseColor}
    highlightColor={skeletonHighlightColor}
  />
);

export const RegisterDataForm = ({
  requestSearchByCnpj,
  setModalDeleteState,
  requestEntitiesDelete,
  requestGetAddressViaZipCode,
  setCitiesForSelectOptionsData,
  options,
  entityId,
  validation,
  marginYButton,
  zipCodeSchema,
  loadingArchive,
  searchByCnpjData,
  modalDeleteState,
  loadingSearchByCnpj,
  hasChangedAddressFields,
  citiesForSelectOptionsData,
  requestGetAllCitiesForSelectOptions,
  loading = false,
}: IProps) => {
  const schoolPeriodError =
    typeof validation?.errors?.schoolPeriods === 'string'
      ? validation.errors.schoolPeriods
      : undefined;

  return (
    <>
      <ModalArchive
        loading={loadingArchive}
        isOpen={modalDeleteState}
        handleDelete={() => {
          requestEntitiesDelete({ id: entityId });
          setModalDeleteState(!modalDeleteState);
        }}
        goBack={() => setModalDeleteState(!modalDeleteState)}
      />
      <Row>
        <Col md={12}>
          <div className="my-2">
            <OverlineTitle title="Dados principais" />
          </div>
          <Row>
            <Col md={4} className={`${marginYButton}`}>
              <Label htmlFor="entity" className="form-label">
                Tipo:
              </Label>
              {loading ? (
                <FieldSkeleton />
              ) : (
                <>
                  <Select
                    invalid={!!(validation.touched && validation.errors?.type)}
                    isClearable
                    id="type"
                    name="type"
                    placeholder=""
                    value={validation.values?.type}
                    onChange={(e: any) =>
                      validation.handleChange(
                        convertEventSelectToTarget('type', e),
                      )
                    }
                    onBlur={validation.handleBlur}
                    options={options}
                  />
                  <FormFeedBack
                    error={validation.errors?.type as any}
                    isInvalid={!!(validation.touched && validation.errors?.type)}
                  />
                </>
              )}
            </Col>
            <Col md={4} className={`${marginYButton}`}>
              <Label htmlFor="cnpj" className="form-label">
                CNPJ
              </Label>
              {loading ? (
                <FieldSkeleton />
              ) : (
                <>
                  <CnpjOrCpfInput
                    id="cnpj"
                    name="cnpj"
                    onChange={(e) => {
                      validation.handleChange(e);
                      if (e.target.value && e.target.value.length === 18) {
                        requestSearchByCnpj({
                          cnpj: removeCnpjMask(e.target.value),
                        });
                      }
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.cnpj}
                    invalid={
                      !!(validation?.touched?.cnpj && validation?.errors?.cnpj)
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.cnpj}
                    isInvalid={
                      !!(validation?.touched?.cnpj && validation?.errors?.cnpj)
                    }
                  />
                </>
              )}
            </Col>
            <Col md={4} className={`${marginYButton}`}>
              <Label htmlFor="apiIntegrationCode" className="form-label">
                Código de Integração (API)
              </Label>
              {loading ? (
                <FieldSkeleton />
              ) : (
                <Input
                  id="apiIntegrationCode"
                  type="text"
                  name="apiIntegrationCode"
                  className="form-control"
                  value={validation.values.apiIntegrationCode}
                  onChange={validation.handleChange}
                />
              )}
            </Col>
            <Col md={6} className={`${marginYButton}`}>
              <Label htmlFor="corporateName" className="form-label">
                Razão Social
              </Label>
              {loading ? (
                <FieldSkeleton />
              ) : (
                <>
                  <Input
                    id="corporateName"
                    disabled={
                      STATUSPAGE.LOADING === loadingSearchByCnpj ? true : false
                    }
                    type="text"
                    name="corporateName"
                    className="form-control"
                    value={validation.values.corporateName}
                    onChange={validation.handleChange}
                    invalid={
                      validation.touched.corporateName &&
                      !!validation.errors.corporateName
                    }
                  />

                  <FormFeedBack
                    error={validation?.errors?.corporateName}
                    isInvalid={
                      !!(
                        validation?.touched?.corporateName &&
                        validation?.errors?.corporateName
                      )
                    }
                  />
                </>
              )}
            </Col>
            <Col md={6} className={`${marginYButton}`}>
              <Label htmlFor="tradeName" className="form-label">
                Nome Fantasia
              </Label>
              {loading ? (
                <FieldSkeleton />
              ) : (
                <>
                  <Input
                    id="tradeName"
                    type="text"
                    disabled={
                      STATUSPAGE.LOADING === loadingSearchByCnpj ? true : false
                    }
                    name="tradeName"
                    className="form-control"
                    value={validation.values.tradeName}
                    onChange={validation.handleChange}
                    invalid={
                      validation.touched.tradeName &&
                      !!validation.errors.tradeName
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.tradeName}
                    isInvalid={
                      !!(
                        validation?.touched?.tradeName &&
                        validation?.errors?.tradeName
                      )
                    }
                  />
                </>
              )}
            </Col>
            <Col md={6} className={`${marginYButton}`}>
              <Label htmlFor="representativeName" className="form-label">
                Representante
              </Label>
              {loading ? (
                <FieldSkeleton />
              ) : (
                <Input
                  id="representativeName"
                  type="text"
                  name="representative.name"
                  className="form-control"
                  value={validation.values.representative.name}
                  onChange={validation.handleChange}
                />
              )}
            </Col>
            <Col md={6} className={`${marginYButton}`}>
              <Label htmlFor="representativeRole" className="form-label">
                Cargo
              </Label>
              {loading ? (
                <FieldSkeleton />
              ) : (
                <Input
                  id="representativeRole"
                  type="text"
                  name="representative.role"
                  className="form-control"
                  value={validation.values.representative.role}
                  onChange={validation.handleChange}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <div className="mt-2">
          <OverlineTitle title="Contatos" />
        </div>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="phone" className="form-label">
            Telefone
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <TelPhone
              id="phone"
              name="phone"
              value={`${validation.values.phone}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const inputValue = {
                  target: {
                    name: 'phone',
                    value: e,
                  },
                };
                validation.handleChange(inputValue);
              }}
              onBlur={validation.handleBlur}
            />
          )}
        </Col>

        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="email" className="form-label">
            E-mail
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <Input
              id="email"
              type="email"
              name="email"
              className="form-control"
              value={validation.values.email}
              onChange={validation.handleChange}
              invalid={validation.touched.email && !!validation.errors.email}
            />
          )}
        </Col>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="site" className="form-label">
            Site
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <Input
              id="site"
              type="text"
              name="site"
              className="form-control"
              value={validation.values.site}
              onChange={validation.handleChange}
            />
          )}
        </Col>
      </Row>
      <Row>
        <div className="mt-2">
          <OverlineTitle title="Suporte do whatsapp BOT" />
        </div>

        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="phoneNumber" className="form-label">
            WhatsApp
          </Label>

          {loading ? (
            <FieldSkeleton />
          ) : (
            <TelPhone
              id="phoneNumber"
              name="support.phoneNumber"
              value={`${validation.values.support.phoneNumber}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const inputValue = {
                  target: {
                    name: 'support.phoneNumber',
                    value: e,
                  },
                };
                validation.handleChange(inputValue);
              }}
              onBlur={validation.handleBlur}
            />
          )}
        </Col>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="contactName" className="form-label">
            Nome de Contato
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <Input
              id="contactName"
              type="text"
              name="support.contactName"
              className="form-control"
              value={validation.values.support.contactName}
              onChange={validation.handleChange}
            />
          )}
        </Col>
      </Row>
      <Row>
        <div className="mt-2">
          <OverlineTitle title="Endereço" />
        </div>

        <Col md={2} className={`${marginYButton}`}>
          <Label htmlFor="zipCode">CEP</Label>

          {loading ? (
            <FieldSkeleton />
          ) : (
            <>
              <ZipCodeInput
                changeInputs={validation.handleChange}
                id="zipCode"
                disabled={
                  STATUSPAGE.LOADING === loadingSearchByCnpj ? true : false
                }
                name="address.zipCode"
                onChange={(e) => {
                  const { value } = e.target;

                  if (
                    value &&
                    value.length ===
                      getZipCodeNumOfDigits({
                        withSpecialCharacters: true,
                      }) &&
                    zipCodeSchema.isValidSync(value)
                  ) {
                    if (
                      removeZipCodeMask(value) !==
                      removeZipCodeMask(searchByCnpjData?.zipCode)
                    )
                      requestGetAddressViaZipCode({
                        zipCode: removeZipCodeMask(value),
                      });
                  }
                  validation?.handleChange(e);
                }}
                onBlur={validation?.handleBlur}
                className="form-control"
                value={validation?.values?.address?.zipCode}
                invalid={
                  !!(
                    validation?.touched?.address?.zipCode &&
                    validation?.errors?.address?.zipCode
                  )
                }
              />
              <FormFeedBack
                error={validation?.errors?.address?.zipCode}
                isInvalid={
                  !!(
                    validation?.touched?.address?.zipCode &&
                    validation?.errors?.address?.zipCode
                  )
                }
              />
            </>
          )}
        </Col>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="street" className="form-label">
            Logradouro
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <>
              <Input
                id="street"
                type="text"
                disabled={
                  STATUSPAGE.LOADING === loadingSearchByCnpj ? true : false
                }
                name="address.street"
                className="form-control"
                value={validation.values.address.street}
                onChange={validation.handleChange}
                invalid={
                  validation.touched?.address?.street &&
                  !!validation.errors?.address?.street
                }
              />
              <FormFeedBack
                error={validation?.errors?.address?.street}
                isInvalid={
                  !!(
                    validation?.touched?.address?.street &&
                    validation?.errors?.address?.street
                  )
                }
              />
            </>
          )}
        </Col>
        <Col md={2} className={`${marginYButton}`}>
          <Label htmlFor="number" className="form-label">
            Número
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <>
              <Input
                id="number"
                type="text"
                name="address.number"
                className="form-control"
                value={validation.values.address.number}
                onChange={validation.handleChange}
                invalid={
                  validation.touched?.address?.number &&
                  !!validation.errors?.address?.number
                }
              />
              <FormFeedBack
                error={validation?.errors?.address?.number}
                isInvalid={
                  !!(
                    validation?.touched?.address?.number &&
                    validation?.errors?.address?.number
                  )
                }
              />
            </>
          )}
        </Col>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="complement" className="form-label">
            Complemento
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <Input
              id="complement"
              type="text"
              name="address.complement"
              className="form-control"
              value={validation.values.address.complement}
              onChange={validation.handleChange}
            />
          )}
        </Col>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="neighborhood" className="form-label">
            Bairro
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <>
              <Input
                id="neighborhood"
                disabled={
                  STATUSPAGE.LOADING === loadingSearchByCnpj ? true : false
                }
                type="text"
                name="address.neighborhood"
                className="form-control"
                value={validation.values.address.neighborhood}
                onChange={validation.handleChange}
                invalid={
                  validation.touched?.address?.neighborhood &&
                  !!validation.errors?.address?.neighborhood
                }
              />
              <FormFeedBack
                error={validation?.errors?.address?.neighborhood}
                isInvalid={
                  !!(
                    validation?.touched?.address?.neighborhood &&
                    validation?.errors?.address?.neighborhood
                  )
                }
              />
            </>
          )}
        </Col>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="city" className="form-label">
            Cidade
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <>
              <SelectRemote
                name="address.city"
                isDisabled={
                  STATUSPAGE.LOADING === loadingSearchByCnpj ? true : false
                }
                clearOptions={() => setCitiesForSelectOptionsData([])}
                functionSearch={(e) => {
                  requestGetAllCitiesForSelectOptions({ search: e });
                }}
                inputId={'address.city'}
                onChange={(e) => {
                  hasChangedAddressFields.current = true;
                  validation?.handleChange(
                    convertEventSelectToTarget('address.city', e),
                  );
                }}
                onBlur={validation?.handleBlur}
                options={formatDataToLabelValue(
                  citiesForSelectOptionsData as never,
                  'id',
                  'name',
                )}
                value={validation?.values?.address.city}
                loading={false}
                isInvalid={
                  !!(
                    validation?.touched?.address?.city &&
                    validation?.errors?.address?.city
                  )
                }
              />
              <FormFeedBack
                error={validation?.errors?.address?.city as any}
                isInvalid={
                  !!(
                    validation?.touched?.address?.city &&
                    validation?.errors?.address?.city
                  )
                }
              />
            </>
          )}
        </Col>
      </Row>
      <Row>
        <div className="mt-2">
          <OverlineTitle title="Período Letivo" />
        </div>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="firstPeriod" className="form-label d-block">
            Primeiro Período
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <>
              <div className="d-flex gap-2 mt-1">
                <Input
                  id="firstPeriodStartDate"
                  name="schoolPeriods.firstPeriod.startDate"
                  type="text"
                  maxLength={5}
                  placeholder="DD/MM"
                  value={
                    validation.values.schoolPeriods?.firstPeriod?.startDate ||
                    ''
                  }
                  onChange={(e) =>
                    validation.setFieldValue(
                      'schoolPeriods.firstPeriod.startDate',
                      applyDayMonthMask(e.target.value),
                    )
                  }
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation?.touched?.schoolPeriods?.firstPeriod
                        ?.startDate &&
                      validation?.errors?.schoolPeriods?.firstPeriod?.startDate
                    )
                  }
                />
                <Input
                  id="firstPeriodEndDate"
                  name="schoolPeriods.firstPeriod.endDate"
                  type="text"
                  maxLength={5}
                  placeholder="DD/MM"
                  value={
                    validation.values.schoolPeriods?.firstPeriod?.endDate || ''
                  }
                  onChange={(e) =>
                    validation.setFieldValue(
                      'schoolPeriods.firstPeriod.endDate',
                      applyDayMonthMask(e.target.value),
                    )
                  }
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation?.touched?.schoolPeriods?.firstPeriod
                        ?.endDate &&
                      validation?.errors?.schoolPeriods?.firstPeriod?.endDate
                    )
                  }
                />
              </div>
              <FormFeedBack
                error={
                  (validation?.errors?.schoolPeriods?.firstPeriod as any)
                    ?.startDate ||
                  (validation?.errors?.schoolPeriods?.firstPeriod as any)
                    ?.endDate ||
                  (validation?.errors?.schoolPeriods?.firstPeriod as any)
                }
                isInvalid={
                  !!(
                    validation?.touched?.schoolPeriods?.firstPeriod &&
                    validation?.errors?.schoolPeriods?.firstPeriod
                  )
                }
              />
            </>
          )}
        </Col>
        <Col md={4} className={`${marginYButton}`}>
          <Label htmlFor="secondPeriod" className="form-label d-block">
            Segundo Período
          </Label>
          {loading ? (
            <FieldSkeleton />
          ) : (
            <>
              <div className="d-flex gap-2 mt-1">
                <Input
                  id="secondPeriodStartDate"
                  name="schoolPeriods.secondPeriod.startDate"
                  type="text"
                  maxLength={5}
                  placeholder="DD/MM"
                  value={
                    validation.values.schoolPeriods?.secondPeriod?.startDate ||
                    ''
                  }
                  onChange={(e) =>
                    validation.setFieldValue(
                      'schoolPeriods.secondPeriod.startDate',
                      applyDayMonthMask(e.target.value),
                    )
                  }
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation?.touched?.schoolPeriods?.secondPeriod
                        ?.startDate &&
                      validation?.errors?.schoolPeriods?.secondPeriod
                        ?.startDate
                    )
                  }
                />
                <Input
                  id="secondPeriodEndDate"
                  name="schoolPeriods.secondPeriod.endDate"
                  type="text"
                  maxLength={5}
                  placeholder="DD/MM"
                  value={
                    validation.values.schoolPeriods?.secondPeriod?.endDate ||
                    ''
                  }
                  onChange={(e) =>
                    validation.setFieldValue(
                      'schoolPeriods.secondPeriod.endDate',
                      applyDayMonthMask(e.target.value),
                    )
                  }
                  onBlur={validation.handleBlur}
                  invalid={
                    !!(
                      validation?.touched?.schoolPeriods?.secondPeriod
                        ?.endDate &&
                      validation?.errors?.schoolPeriods?.secondPeriod?.endDate
                    )
                  }
                />
              </div>
              <FormFeedBack
                error={
                  (validation?.errors?.schoolPeriods?.secondPeriod as any)
                    ?.startDate ||
                  (validation?.errors?.schoolPeriods?.secondPeriod as any)
                    ?.endDate ||
                  (validation?.errors?.schoolPeriods?.secondPeriod as any)
                }
                isInvalid={
                  !!(
                    validation?.touched?.schoolPeriods?.secondPeriod &&
                    validation?.errors?.schoolPeriods?.secondPeriod
                  )
                }
              />
            </>
          )}
        </Col>
        <Col md={8}>
          <FormFeedBack
            error={schoolPeriodError}
            isInvalid={!!schoolPeriodError}
          />
        </Col>
      </Row>
    </>
  );
};
